"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
// lucide-react aivaji react-icons vapra
import { FaTimes as XIcon } from "react-icons/fa"

import { cn } from "../../lib/utils.js"

const Sheet = ({ ...props }) => {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />
}
Sheet.displayName = "Sheet"

const SheetTrigger = ({ ...props }) => {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}
SheetTrigger.displayName = "SheetTrigger"

const SheetClose = ({ ...props }) => {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}
SheetClose.displayName = "SheetClose"

const SheetPortal = ({ ...props }) => {
    return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}
SheetPortal.displayName = "SheetPortal"


const SheetOverlay = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Overlay
        ref={ref}
        data-slot="sheet-overlay"
        className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
            className
        )}
        {...props}
    />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName


const SheetContent = React.forwardRef(({ className, children, side = "right", ...props }, ref) => (
    <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
            ref={ref}
            data-slot="sheet-content"
            className={cn(
                "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
                side === "right" &&
                "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
                side === "left" &&
                "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
                side === "top" &&
                "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b",
                side === "bottom" &&
                "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t",
                className
            )}
            {...props}
        >
            {children}
            <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
        </SheetPrimitive.Content>
    </SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName


const SheetHeader = ({ className, ...props }) => (
    <div
        data-slot="sheet-header"
        className={cn("flex flex-col gap-1.5 p-4 text-center sm:text-left", className)}
        {...props}
    />
)
SheetHeader.displayName = "SheetHeader"


const SheetFooter = ({ className, ...props }) => (
    <div
        data-slot="sheet-footer"
        className={cn("mt-auto flex flex-col gap-2 p-4 sm:flex-row sm:justify-end", className)}
        {...props}
    />
)
SheetFooter.displayName = "SheetFooter"


const SheetTitle = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Title
        ref={ref}
        data-slot="sheet-title"
        className={cn("text-foreground text-lg font-semibold", className)}
        {...props}
    />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName


const SheetDescription = React.forwardRef(({ className, ...props }, ref) => (
    <SheetPrimitive.Description
        ref={ref}
        data-slot="sheet-description"
        className={cn("text-muted-foreground text-sm", className)}
        {...props}
    />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName


export {
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
}